import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertTriangle, CheckCircle, Copy, Hash, Phone } from 'lucide-react';
import { HAND_RAISE_SCRIPT, validateMergeTags, resolveMergeTags, generateSHA256 } from '@/utils/handRaiseScript';
import { toast } from 'sonner';

interface HandRaisePreflightDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: {
    userName: string;
    companyName: string;
    industry: string;
    prospect_name?: string;
    scriptContent?: string;
  };
  onProceed: (resolvedScript: string, checksum: string, phoneNumber: string) => void;
}

export function HandRaisePreflightDialog({
  open,
  onOpenChange,
  context,
  onProceed
}: HandRaisePreflightDialogProps) {
  const [resolvedScript, setResolvedScript] = useState('');
  const [checksum, setChecksum] = useState('');
  const [validation, setValidation] = useState<{ valid: boolean; missing: string[] }>({ valid: false, missing: [] });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Phone number validation function
  const validatePhoneNumber = (phone: string): boolean => {
    // Remove all non-digits
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Check if it's a valid US number (10 digits) or international (7-15 digits with country code)
    if (digitsOnly.length === 10 && digitsOnly[0] !== '0' && digitsOnly[0] !== '1') {
      // US number without country code
      return true;
    } else if (digitsOnly.length >= 7 && digitsOnly.length <= 15) {
      // International number with country code
      return true;
    }
    return false;
  };

  // Format phone number to E.164
  const formatToE164 = (phone: string): string => {
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    } else if (digitsOnly.length > 10 && !digitsOnly.startsWith('1')) {
      return `+${digitsOnly}`;
    } else if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
      return `+${digitsOnly}`;
    }
    return `+${digitsOnly}`;
  };

  const handlePhoneChange = (value: string) => {
    setPhoneNumber(value);
    
    if (!value.trim()) {
      setPhoneError('Phone number is required');
      return;
    }
    
    if (!validatePhoneNumber(value)) {
      setPhoneError('Enter a valid phone number (10 digits for US or international with country code)');
      return;
    }
    
    setPhoneError('');
  };

  useEffect(() => {
    if (open) {
      const validation = validateMergeTags(context);
      setValidation(validation);
      
      if (validation.valid) {
        const resolved = resolveMergeTags(HAND_RAISE_SCRIPT, context);
        setResolvedScript(resolved);
        
        generateSHA256(resolved).then(hash => {
          setChecksum(hash);
        });
      }
    }
  }, [open, context]);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleProceed = () => {
    if (validation.valid && resolvedScript && checksum && phoneNumber && !phoneError) {
      const e164Phone = formatToE164(phoneNumber);
      onProceed(resolvedScript, checksum, e164Phone);
      onOpenChange(false);
    }
  };

  const canProceed = validation.valid && resolvedScript && checksum && phoneNumber && !phoneError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby="preflight-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Hand Raise (Safe Mode) - PREFLIGHT VERIFICATION
          </DialogTitle>
          <p id="preflight-description" className="text-sm text-muted-foreground mt-2">
            Verify script content and generate checksum before initiating test call
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Phone Number Input */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Test Phone Number
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label htmlFor="phone-input" className="text-sm font-medium">
                  Enter a valid phone number for the test call
                </Label>
                <Input
                  id="phone-input"
                  value={phoneNumber}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="(555) 123-4567 or +1234567890"
                  className={phoneError ? "border-red-500" : ""}
                />
                {phoneError && (
                  <p className="text-red-500 text-xs mt-1">{phoneError}</p>
                )}
                {phoneNumber && !phoneError && (
                  <p className="text-green-600 text-xs mt-1">
                    ✓ Will call: {formatToE164(phoneNumber)}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Validation Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                {validation.valid ? (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                )}
                Merge Tag Validation
              </CardTitle>
            </CardHeader>
            <CardContent>
              {validation.valid ? (
                <div className="text-green-700 text-sm">
                  ✓ All required merge tags present
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-red-700 text-sm">
                    ❌ Missing required merge tags:
                  </div>
                  <div className="flex gap-2">
                    {validation.missing.map(tag => (
                      <Badge key={tag} variant="destructive">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Script Preview */}
          {validation.valid && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Hash className="h-4 w-4" />
                    SHA-256 Checksum
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(checksum, 'Checksum')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <code className="text-xs bg-muted p-2 rounded block font-mono">
                    {checksum}
                  </code>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center justify-between">
                    Resolved System Prompt (EXACT)
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(resolvedScript, 'System Prompt')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={resolvedScript}
                    readOnly
                    className="font-mono text-xs min-h-[300px]"
                  />
                </CardContent>
              </Card>
            </>
          )}

          {/* Actions */}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            
            {canProceed ? (
              <div className="space-x-2">
                <Button
                  onClick={handleProceed}
                  className="bg-green-600 hover:bg-green-700"
                >
                  ✓ PROCEED WITH EXACT SCRIPT
                </Button>
              </div>
            ) : (
              <Button disabled variant="destructive">
                {!validation.valid ? '❌ FIX MERGE TAGS FIRST' : 
                 !phoneNumber || phoneError ? '❌ ENTER VALID PHONE NUMBER' : 
                 '❌ VALIDATION REQUIRED'}
              </Button>
            )}
          </div>

          {/* Warning */}
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="pt-4">
              <div className="text-amber-800 text-sm space-y-2">
                <div className="font-semibold">⚠️ ZERO TOLERANCE POLICY</div>
                <div>
                  This preview shows the EXACT systemPrompt that will be sent to VAPI.
                  If ANY text appears that is not from the "WORD OF GOD" script, 
                  the operation will be aborted immediately.
                </div>
                <div className="font-semibold">
                  Only merge tag substitution is allowed - no additions, wrappers, or modifications.
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}